import React, { useState, useEffect } from "react";
import { X, Calculator, AlertTriangle, Calendar } from "lucide-react";
import {
  calcolaRateizzazione,
  formatEuro,
} from "../../utils/ravvedimentoCalc";
import { MAX_RATE_GIUGNO } from "../../constants/ravvedimento";

export default function RateizzazioneCalculator({
  isOpen,
  onClose,
  darkMode = false,
}) {
  const currentYear = new Date().getFullYear();
  const [importo, setImporto] = useState("");
  const [numeroRate, setNumeroRate] = useState(3);
  const [anno, setAnno] = useState(currentYear);
  const [risultato, setRisultato] = useState(null);
  const [errors, setErrors] = useState({});

  // Reset quando si apre il modal
  useEffect(() => {
    if (isOpen) {
      setImporto("");
      setNumeroRate(3);
      setAnno(currentYear);
      setRisultato(null);
      setErrors({});
    }
  }, [isOpen, currentYear]);

  // Ricalcola automaticamente quando cambiano i parametri
  useEffect(() => {
    if (importo && parseAmount(importo) > 0) {
      const result = calcolaRateizzazione(parseAmount(importo), numeroRate, anno);
      setRisultato(result);
    } else {
      setRisultato(null);
    }
  }, [importo, numeroRate, anno]);

  if (!isOpen) return null;

  const parseAmount = (str) => {
    if (!str) return 0;
    const cleaned = str.replace(/\s/g, "").replace(",", ".");
    return parseFloat(cleaned) || 0;
  };

  const handleAmountChange = (e) => {
    const value = e.target.value.replace(/[^0-9,.]/g, "");
    setImporto(value);
    setErrors({});
  };

  const handleReset = () => {
    setImporto("");
    setNumeroRate(3);
    setAnno(currentYear);
    setRisultato(null);
    setErrors({});
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className={`relative w-full max-w-xl rounded-2xl shadow-xl ${
            darkMode ? "bg-neutral-900" : "bg-white"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div
            className={`flex items-center justify-between px-6 py-4 border-b ${
              darkMode ? "border-neutral-800" : "border-neutral-200"
            }`}
          >
            <div className="flex items-center gap-2">
              <Calendar
                className={`w-5 h-5 ${
                  darkMode ? "text-forfy-300" : "text-forfy-600"
                }`}
              />
              <h2
                className={`text-lg font-semibold ${
                  darkMode ? "text-white" : "text-neutral-900"
                }`}
              >
                Calcola Rateizzazione Saldo
              </h2>
            </div>
            <button
              onClick={onClose}
              className={`p-1.5 rounded-lg transition-colors ${
                darkMode
                  ? "hover:bg-neutral-800 text-neutral-500"
                  : "hover:bg-neutral-100 text-neutral-500"
              }`}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <div className="p-6 space-y-5">
            {/* Form inputs */}
            <div className="space-y-4">
              {/* Importo */}
              <div>
                <label
                  htmlFor="rateImporto"
                  className={`block text-sm font-medium mb-1.5 ${
                    darkMode ? "text-neutral-400" : "text-neutral-700"
                  }`}
                >
                  Importo saldo da rateizzare
                </label>
                <div className="relative">
                  <span
                    className={`absolute left-3 top-1/2 -translate-y-1/2 text-sm ${
                      darkMode ? "text-neutral-500" : "text-neutral-500"
                    }`}
                  >
                    €
                  </span>
                  <input
                    type="text"
                    inputMode="decimal"
                    id="rateImporto"
                    value={importo}
                    onChange={handleAmountChange}
                    placeholder="0,00"
                    className={`w-full pl-8 pr-3 py-2.5 border-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-forfy-500 focus:border-forfy-500 ${
                      errors.importo
                        ? darkMode
                          ? "border-red-700 bg-red-900/30"
                          : "border-red-300 bg-red-50"
                        : darkMode
                        ? "border-neutral-700 bg-neutral-800 text-white"
                        : "border-neutral-300 bg-white text-neutral-900"
                    }`}
                  />
                </div>
                {errors.importo && (
                  <p className="mt-1 text-sm text-red-500">{errors.importo}</p>
                )}
              </div>

              {/* Row con numero rate e anno */}
              <div className="grid grid-cols-2 gap-4">
                {/* Numero rate */}
                <div>
                  <label
                    htmlFor="rateNumero"
                    className={`block text-sm font-medium mb-1.5 ${
                      darkMode ? "text-neutral-400" : "text-neutral-700"
                    }`}
                  >
                    Numero rate
                  </label>
                  <select
                    id="rateNumero"
                    value={numeroRate}
                    onChange={(e) => setNumeroRate(Number(e.target.value))}
                    className={`w-full px-3 py-2.5 border-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-forfy-500 focus:border-forfy-500 ${
                      darkMode
                        ? "border-neutral-700 bg-neutral-800 text-white"
                        : "border-neutral-300 bg-white text-neutral-900"
                    }`}
                  >
                    {Array.from({ length: MAX_RATE_GIUGNO }, (_, i) => i + 1).map(
                      (n) => (
                        <option key={n} value={n}>
                          {n} {n === 1 ? "rata (unica soluzione)" : "rate"}
                        </option>
                      )
                    )}
                  </select>
                </div>

                {/* Anno */}
                <div>
                  <label
                    htmlFor="rateAnno"
                    className={`block text-sm font-medium mb-1.5 ${
                      darkMode ? "text-neutral-400" : "text-neutral-700"
                    }`}
                  >
                    Anno scadenze
                  </label>
                  <select
                    id="rateAnno"
                    value={anno}
                    onChange={(e) => setAnno(Number(e.target.value))}
                    className={`w-full px-3 py-2.5 border-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-forfy-500 focus:border-forfy-500 ${
                      darkMode
                        ? "border-neutral-700 bg-neutral-800 text-white"
                        : "border-neutral-300 bg-white text-neutral-900"
                    }`}
                  >
                    {[currentYear - 1, currentYear, currentYear + 1].map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Reset button */}
              <button
                type="button"
                onClick={handleReset}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
                  darkMode
                    ? "bg-neutral-800 text-neutral-400 hover:bg-neutral-700"
                    : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
                }`}
              >
                Reset
              </button>
            </div>

            {/* Risultato - Tabella rate */}
            {risultato && risultato.rate.length > 0 && (
              <div
                className={`rounded-xl border-2 overflow-hidden ${
                  darkMode ? "border-neutral-700" : "border-neutral-200"
                }`}
              >
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr
                        className={
                          darkMode ? "bg-neutral-800" : "bg-neutral-100"
                        }
                      >
                        <th
                          className={`px-3 py-2.5 text-left font-medium ${
                            darkMode ? "text-neutral-300" : "text-neutral-700"
                          }`}
                        >
                          Rata
                        </th>
                        <th
                          className={`px-3 py-2.5 text-left font-medium ${
                            darkMode ? "text-neutral-300" : "text-neutral-700"
                          }`}
                        >
                          Scadenza
                        </th>
                        <th
                          className={`px-3 py-2.5 text-right font-medium ${
                            darkMode ? "text-neutral-300" : "text-neutral-700"
                          }`}
                        >
                          Importo
                        </th>
                        <th
                          className={`px-3 py-2.5 text-right font-medium ${
                            darkMode ? "text-neutral-300" : "text-neutral-700"
                          }`}
                        >
                          Magg.
                        </th>
                        <th
                          className={`px-3 py-2.5 text-right font-medium ${
                            darkMode ? "text-neutral-300" : "text-neutral-700"
                          }`}
                        >
                          Totale
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {risultato.rate.map((rata, idx) => (
                        <tr
                          key={rata.numero}
                          className={
                            darkMode
                              ? idx % 2 === 0
                                ? "bg-neutral-900"
                                : "bg-neutral-800/50"
                              : idx % 2 === 0
                              ? "bg-white"
                              : "bg-neutral-50"
                          }
                        >
                          <td
                            className={`px-3 py-2.5 ${
                              darkMode ? "text-neutral-300" : "text-neutral-700"
                            }`}
                          >
                            {rata.numero}
                          </td>
                          <td
                            className={`px-3 py-2.5 ${
                              darkMode ? "text-neutral-300" : "text-neutral-700"
                            }`}
                          >
                            <span className="font-medium">{rata.scadenza}/{anno}</span>
                          </td>
                          <td
                            className={`px-3 py-2.5 text-right tabular-nums ${
                              darkMode ? "text-neutral-300" : "text-neutral-700"
                            }`}
                          >
                            {formatEuro(rata.importoBase)}
                          </td>
                          <td
                            className={`px-3 py-2.5 text-right tabular-nums ${
                              rata.maggiorazione > 0
                                ? darkMode
                                  ? "text-amber-400"
                                  : "text-amber-600"
                                : darkMode
                                ? "text-neutral-500"
                                : "text-neutral-400"
                            }`}
                          >
                            {rata.maggiorazione > 0
                              ? `+${formatEuro(rata.maggiorazione)}`
                              : "-"}
                          </td>
                          <td
                            className={`px-3 py-2.5 text-right font-medium tabular-nums ${
                              darkMode ? "text-white" : "text-neutral-900"
                            }`}
                          >
                            {formatEuro(rata.totale)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr
                        className={`border-t-2 ${
                          darkMode
                            ? "border-neutral-700 bg-neutral-800"
                            : "border-neutral-200 bg-neutral-100"
                        }`}
                      >
                        <td
                          colSpan={2}
                          className={`px-3 py-3 font-semibold ${
                            darkMode ? "text-white" : "text-neutral-900"
                          }`}
                        >
                          Totale
                        </td>
                        <td
                          className={`px-3 py-3 text-right tabular-nums ${
                            darkMode ? "text-neutral-300" : "text-neutral-700"
                          }`}
                        >
                          {formatEuro(risultato.importoOriginale)}
                        </td>
                        <td
                          className={`px-3 py-3 text-right tabular-nums ${
                            risultato.totaleMaggiorazioni > 0
                              ? darkMode
                                ? "text-amber-400"
                                : "text-amber-600"
                              : darkMode
                              ? "text-neutral-500"
                              : "text-neutral-400"
                          }`}
                        >
                          {risultato.totaleMaggiorazioni > 0
                            ? `+${formatEuro(risultato.totaleMaggiorazioni)}`
                            : "-"}
                        </td>
                        <td
                          className={`px-3 py-3 text-right font-bold tabular-nums text-forfy-500`}
                        >
                          {formatEuro(risultato.totaleComplessivo)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {/* Info maggiorazione */}
                {risultato.totaleMaggiorazioni > 0 && (
                  <div
                    className={`px-4 py-2.5 text-xs border-t ${
                      darkMode
                        ? "border-neutral-700 bg-neutral-800/50 text-neutral-400"
                        : "border-neutral-200 bg-neutral-50 text-neutral-600"
                    }`}
                  >
                    Maggiorazione: 0,33% per ogni mese successivo al primo
                  </div>
                )}
              </div>
            )}

            {/* Placeholder quando non c'è importo */}
            {!risultato && (
              <div
                className={`text-center py-8 rounded-xl border-2 border-dashed ${
                  darkMode
                    ? "border-neutral-700 text-neutral-500"
                    : "border-neutral-300 text-neutral-400"
                }`}
              >
                <Calculator className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Inserisci un importo per vedere il piano rate</p>
              </div>
            )}

            {/* Disclaimer */}
            <div
              className={`flex gap-3 p-3 rounded-lg ${
                darkMode ? "bg-amber-900/20" : "bg-amber-50"
              }`}
            >
              <AlertTriangle
                className={`w-5 h-5 flex-shrink-0 ${
                  darkMode ? "text-amber-400" : "text-amber-600"
                }`}
              />
              <p
                className={`text-xs ${
                  darkMode ? "text-amber-300" : "text-amber-800"
                }`}
              >
                Calcolo indicativo. Per versamenti con ravvedimento, verificare
                sempre gli importi con un commercialista o con l'Agenzia delle
                Entrate.
              </p>
            </div>
          </div>

          {/* Footer */}
          <div
            className={`px-6 py-4 border-t ${
              darkMode ? "border-neutral-800" : "border-neutral-200"
            }`}
          >
            <button
              onClick={onClose}
              className={`w-full px-4 py-2.5 rounded-lg font-medium text-sm transition-colors ${
                darkMode
                  ? "bg-neutral-800 text-neutral-300 hover:bg-neutral-700"
                  : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
              }`}
            >
              Chiudi
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
